<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Purchase Orders</h1>
          <p class="lede">Order stock from suppliers, then receive it against the PO so counts and costs update automatically.</p>
        </div>
        <div class="head-actions">
          <select v-model="statusFilter" @change="load" style="max-width:180px">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partially_received">Partially received</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button class="primary" @click="openNew">+ New PO</button>
        </div>
      </div>

      <div class="card">
        <table v-if="pos.length">
          <thead>
            <tr><th>PO #</th><th>Supplier</th><th>Shop</th><th>Status</th><th class="r">Total</th><th class="r">Received</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="po in pos" :key="po.id" @click="openDetail(po.id)" class="clickable">
              <td><strong>{{ po.po_number }}</strong></td>
              <td>{{ po.supplier_name || '—' }}</td>
              <td>{{ po.shop_name || '—' }}</td>
              <td><span :class="'po-pill ' + po.status">{{ po.status.replace(/_/g, ' ') }}</span></td>
              <td class="r"><strong>KES {{ format(po.total) }}</strong></td>
              <td class="r">{{ Math.round(po.total_received || 0) }} / {{ Math.round(po.total_ordered || 0) }}</td>
              <td class="muted small">{{ po.created_at }}</td>
              <td><button class="secondary small">Open</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No purchase orders yet. Click <strong>+ New PO</strong> to raise your first order.</p>
      </div>
    </div>

    <!-- NEW / EDIT modal -->
    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card wide">
        <h3>{{ form.id ? 'Edit Purchase Order' : 'New Purchase Order' }}</h3>
        <div class="row-2">
          <div>
            <label>Supplier</label>
            <select v-model.number="form.supplierId">
              <option :value="0" disabled>-- pick --</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div>
            <label>Expected delivery</label>
            <input type="date" v-model="form.expectedAt" />
          </div>
        </div>

        <label>Items</label>
        <div class="po-search">
          <input v-model="variantSearch" @input="onVariantSearch" placeholder="Search a variant to add…" />
          <div v-if="variantResults.length" class="po-results">
            <div v-for="v in variantResults" :key="v.id" class="po-result" @click="addLine(v)">
              <div><strong>{{ v.product_name }}</strong> · {{ v.variant_name }}</div>
              <div class="muted small">{{ v.sku }} · Cost KES {{ format(v.cost_price) }}</div>
            </div>
          </div>
        </div>

        <table class="po-items" v-if="form.items.length">
          <thead><tr><th>Item</th><th style="width:80px">Qty</th><th style="width:100px">Unit cost</th><th class="r">Line total</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in form.items" :key="i">
              <td>
                <div>{{ it.product_name }} · {{ it.variant_name }}</div>
                <div class="muted small">{{ it.sku }}</div>
              </td>
              <td><input type="number" v-model.number="it.orderedQty" min="1" style="width:70px; padding: 5px" /></td>
              <td><input type="number" v-model.number="it.unitCost" min="0" step="0.01" style="width:90px; padding: 5px" /></td>
              <td class="r">KES {{ format(it.orderedQty * it.unitCost) }}</td>
              <td><button class="danger small" @click="form.items.splice(i,1)">&times;</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted small center" style="padding:10px 0">Search above to add items.</p>

        <div class="row-2" style="margin-top:12px">
          <div>
            <label>Landed costs (shipping, duty, clearing)</label>
            <input type="number" v-model.number="form.landedCosts" step="0.01" />
          </div>
          <div>
            <label>Notes</label>
            <input v-model="form.notes" />
          </div>
        </div>
        <div class="po-totals">
          <div>Subtotal: <strong>KES {{ format(subtotal) }}</strong></div>
          <div>Landed: <strong>KES {{ format(form.landedCosts) }}</strong></div>
          <div class="total">Total: <strong>KES {{ format(subtotal + form.landedCosts) }}</strong></div>
        </div>

        <div class="modal-actions">
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving || !canSave">{{ saving ? 'Saving…' : 'Save as draft' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- DETAIL / RECEIVE modal -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card wide">
        <div class="detail-head">
          <div>
            <h3>{{ detail.po_number }}</h3>
            <p class="muted">{{ detail.supplier_name }} · {{ detail.shop_name }} ·
              <span :class="'po-pill ' + detail.status">{{ detail.status.replace(/_/g, ' ') }}</span>
            </p>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>

        <table>
          <thead><tr><th>Item</th><th class="r">Ordered</th><th class="r">Received</th><th class="r">Unit cost</th><th class="r">Landed / unit</th><th class="r">Effective cost</th><th class="r" v-if="canReceive">Receive now</th></tr></thead>
          <tbody>
            <tr v-for="it in detail.items" :key="it.id">
              <td>
                <div>{{ it.product_name }} · {{ it.variant_name }}</div>
                <div class="muted small">{{ it.sku }}</div>
              </td>
              <td class="r">{{ Math.round(it.ordered_qty) }}</td>
              <td class="r">{{ Math.round(it.received_qty) }}</td>
              <td class="r">KES {{ format(it.unit_cost) }}</td>
              <td class="r muted">KES {{ format(it.ordered_qty > 0 ? it.allocated_landed / it.ordered_qty : 0) }}</td>
              <td class="r">KES {{ format(effectiveCost(it)) }}</td>
              <td class="r" v-if="canReceive">
                <input type="number" v-model.number="receipts[it.id]" min="0"
                       :max="it.ordered_qty - it.received_qty"
                       :disabled="it.ordered_qty - it.received_qty <= 0"
                       style="width:70px; padding:5px; text-align:right" />
              </td>
            </tr>
          </tbody>
        </table>

        <div class="detail-actions">
          <button v-if="detail.status === 'draft'" class="secondary" @click="sendPO">Mark as sent to supplier</button>
          <button v-if="!['received','cancelled'].includes(detail.status)" class="danger" @click="cancelPO">Cancel PO</button>
          <button v-if="canReceive" class="primary" @click="receivePO" :disabled="receiving || !hasReceipts">
            {{ receiving ? 'Receiving…' : 'Receive selected quantities' }}
          </button>
        </div>
        <p class="err" v-if="detailErr">{{ detailErr }}</p>
        <p class="ok"  v-if="detailOk">{{ detailOk }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

let searchTimer = null;

export default {
  name: 'PurchaseOrders',
  components: { Navigation },
  data() {
    return {
      pos: [],
      statusFilter: '',
      suppliers: [],
      // Form
      showForm: false, saving: false, err: '',
      form: { id: 0, supplierId: 0, shopId: 0, expectedAt: '', landedCosts: 0, notes: '', items: [] },
      variantSearch: '', variantResults: [],
      // Detail
      detail: null, receiving: false, detailErr: '', detailOk: '', receipts: {},
    };
  },
  computed: {
    subtotal() { return this.form.items.reduce((s, it) => s + ((it.orderedQty || 0) * (it.unitCost || 0)), 0); },
    canSave() { return this.form.supplierId > 0 && this.form.items.length > 0; },
    canReceive() { return this.detail && ['sent', 'partially_received'].includes(this.detail.status); },
    hasReceipts() { return Object.values(this.receipts).some(q => q > 0); },
  },
  async mounted() {
    await this.load();
    const { data: sup } = await execute({ function: 'listSuppliers' }, paysol);
    if (sup.success) this.suppliers = sup.data;
  },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    effectiveCost(it) {
      const perUnit = it.ordered_qty > 0 ? Number(it.allocated_landed) / Number(it.ordered_qty) : 0;
      return Number(it.unit_cost) + perUnit;
    },
    async load() {
      const { data } = await execute({ function: 'listPurchaseOrders', status: this.statusFilter }, paysol);
      if (data.success) this.pos = data.data;
    },
    openNew() {
      this.form = { id: 0, supplierId: 0, shopId: parseInt(sessionStorage.getItem('defaultShopId') || '0', 10), expectedAt: '', landedCosts: 0, notes: '', items: [] };
      this.variantSearch = ''; this.variantResults = []; this.err = ''; this.showForm = true;
    },
    onVariantSearch() {
      clearTimeout(searchTimer);
      const q = this.variantSearch.trim();
      if (q.length < 2) { this.variantResults = []; return; }
      searchTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listProducts', search: q }, paysol);
        if (!data.success) return;
        // Flatten: for each product, we need its variants. Get product detail for each.
        const results = [];
        for (const p of data.data.slice(0, 5)) {
          const { data: pd } = await execute({ function: 'getProduct', id: p.id }, paysol);
          if (pd.success) {
            for (const v of pd.data.variants || []) {
              results.push({
                id: v.id, product_name: p.name, variant_name: v.variant_name, sku: v.sku, cost_price: v.cost_price
              });
              if (results.length >= 12) break;
            }
          }
          if (results.length >= 12) break;
        }
        this.variantResults = results;
      }, 300);
    },
    addLine(v) {
      if (this.form.items.some(i => i.variantId === v.id)) return;
      this.form.items.push({
        variantId: v.id, product_name: v.product_name, variant_name: v.variant_name,
        sku: v.sku, orderedQty: 1, unitCost: parseFloat(v.cost_price) || 0,
      });
      this.variantSearch = ''; this.variantResults = [];
    },
    async save() {
      this.saving = true; this.err = '';
      try {
        const payload = {
          function: 'savePurchaseOrder',
          id: this.form.id, supplierId: this.form.supplierId, shopId: this.form.shopId,
          expectedAt: this.form.expectedAt, landedCosts: this.form.landedCosts, notes: this.form.notes,
          items: this.form.items.map(it => ({ variantId: it.variantId, orderedQty: it.orderedQty, unitCost: it.unitCost })),
        };
        const { data } = await execute(payload, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async openDetail(id) {
      const { data } = await execute({ function: 'getPurchaseOrder', id }, paysol);
      if (!data.success) return;
      this.detail = data.data;
      this.receipts = {};
      // Pre-fill receipts with outstanding quantity if PO is ready-to-receive
      if (['sent', 'partially_received'].includes(this.detail.status)) {
        this.detail.items.forEach(it => {
          this.receipts[it.id] = Math.max(0, it.ordered_qty - it.received_qty);
        });
      }
      this.detailErr = ''; this.detailOk = '';
    },
    async sendPO() {
      const { data } = await execute({ function: 'sendPurchaseOrder', id: this.detail.id }, paysol);
      if (data.success) { await this.load(); await this.openDetail(this.detail.id); }
      else this.detailErr = data.message;
    },
    async cancelPO() {
      if (!confirm('Cancel this PO?')) return;
      const { data } = await execute({ function: 'cancelPurchaseOrder', id: this.detail.id }, paysol);
      if (data.success) { this.detail = null; await this.load(); }
      else this.detailErr = data.message;
    },
    async receivePO() {
      this.receiving = true; this.detailErr = ''; this.detailOk = '';
      const items = Object.entries(this.receipts)
        .filter(([, q]) => q > 0)
        .map(([poItemId, receivedQty]) => ({ poItemId: parseInt(poItemId), receivedQty }));
      try {
        const { data } = await execute({ function: 'receivePurchaseOrder', id: this.detail.id, items }, paysol);
        if (!data.success) { this.detailErr = data.message; return; }
        this.detailOk = 'Stock received. Status now: ' + data.data.status;
        await this.load();
        await this.openDetail(this.detail.id);
      } catch (e) { this.detailErr = e.response?.data?.message || 'Receive failed.'; }
      finally { this.receiving = false; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; max-width: 600px; }
.head-actions { display: flex; gap: 8px; align-items: center; }
.clickable { cursor: pointer; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
button.small { padding: 4px 10px; font-size: 11px; }
.po-pill {
  display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.po-pill.draft { background: rgba(139,164,196,0.15); color: var(--paysol-muted); }
.po-pill.sent { background: rgba(79,195,247,0.15); color: #4FC3F7; }
.po-pill.partially_received { background: rgba(245,158,11,0.15); color: var(--paysol-warning); }
.po-pill.received { background: rgba(16,185,129,0.15); color: var(--paysol-success); }
.po-pill.cancelled { background: rgba(239,68,68,0.15); color: var(--paysol-danger); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 860px; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.ok  { color: var(--paysol-success); font-size: 12px; margin-top: 8px; }
.po-search { position: relative; }
.po-results { position: absolute; z-index: 5; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 6px; margin-top: 4px; max-height: 240px; overflow-y: auto; width: 100%; box-shadow: 0 8px 20px rgba(0,0,0,0.15); }
.po-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--paysol-line); }
.po-result:hover { background: var(--paysol-teal-glow); }
.po-result:last-child { border-bottom: none; }
.po-items { margin: 10px 0; }
.po-totals { display: flex; justify-content: flex-end; gap: 20px; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--paysol-line); font-size: 13px; }
.po-totals .total { color: var(--paysol-teal); font-size: 16px; }
.detail-head { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
.detail-head h3 { margin: 0 0 4px; color: var(--paysol-teal); }
.detail-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); flex-wrap: wrap; }
</style>
